import { COMMENTS_BATCH_SIZE, type MediaComment, type MediaCommentsResponse } from '../api/media-comments';
import type { SeedingGroup } from './group-seeding';
import { pickTopComments, type CommentFilter } from './pick-comments';

/**
 * Комментарии к посевам, уже отобранным в отчёт.
 *
 * Спрашиваются только у тех копий, под которыми комментарии есть: счётчик
 * приходит вместе с метриками, и ходить в Graph API за пустотой незачем — под
 * посевом из шести публикаций их обычно две-три.
 *
 * И только по десятку посевов, которые пойдут в файл: у остальных комментарии
 * никто не увидит, а каждая публикация — это до четырёх страниц Graph API.
 *
 * Ролики TikTok спрашиваются своей ручкой (fetchTikTokComments), но отбор
 * общий: одинаковый текст под копией в Instagram и в TikTok — один комментарий.
 */

export type AttachCommentsDeps = {
  /** Не больше COMMENTS_BATCH_SIZE публикаций за вызов. */
  fetchComments: (items: Array<{ mediaId: string; username: string }>) => Promise<MediaCommentsResponse>;
  /** Не больше COMMENTS_BATCH_SIZE роликов за вызов; не задано — у TikTok комментариев не будет. */
  fetchTikTokComments?: (items: Array<{ videoId: string; username: string }>) => Promise<MediaCommentsResponse>;
};

export type AttachCommentsOptions = {
  /** Сколько комментариев оставить под каждым посевом. */
  count: number;
  /** Отсев по содержанию; не задан — порядок чисто по лайкам. */
  filter?: CommentFilter;
};

export type AttachCommentsResult = {
  /** Те же посевы в том же порядке, но с отобранными комментариями. */
  groups: SeedingGroup[];
  errors: Array<{ mediaId: string; error: string }>;
  /** Публикации, где комментариев больше, чем сервер успел прочитать. */
  truncated: string[];
};

export async function attachSeedingComments(
  groups: SeedingGroup[],
  deps: AttachCommentsDeps,
  options: AttachCommentsOptions,
  onProgress: (message: string) => void = () => {},
): Promise<AttachCommentsResult> {
  // comments === null — метрика не пришла; под такой публикацией комментарии
  // могут быть, и спросить про них стоит. Ноль — точно пусто.
  // Публикация без mediaId — это не «нет комментариев», а сбой выше по течению;
  // пустой id завернул бы всю пачку целиком, поэтому его отсеиваем здесь.
  const wanted = groups.flatMap((group) =>
    group.items
      .filter((item) => item.comments !== 0 && item.mediaId)
      .map((item) => ({ mediaId: item.mediaId, username: item.username, tiktok: item.platform === 'tiktok' })),
  );
  if (wanted.length === 0) {
    return { groups: groups.map((g) => ({ ...g, comments: [] })), errors: [], truncated: [] };
  }

  const byMedia = new Map<string, MediaComment[]>();
  const errors: AttachCommentsResult['errors'] = [];
  const truncated: string[] = [];
  const fetchTikTok = deps.fetchTikTokComments;
  const batches: Array<{ size: number; run: () => Promise<MediaCommentsResponse> }> = [];
  const instagram = wanted.filter((w) => !w.tiktok).map(({ mediaId, username }) => ({ mediaId, username }));
  const tiktok = wanted.filter((w) => w.tiktok).map(({ mediaId, username }) => ({ videoId: mediaId, username }));
  for (let i = 0; i < instagram.length; i += COMMENTS_BATCH_SIZE) {
    const part = instagram.slice(i, i + COMMENTS_BATCH_SIZE);
    batches.push({ size: part.length, run: () => deps.fetchComments(part) });
  }
  for (let i = 0; i < tiktok.length; i += COMMENTS_BATCH_SIZE) {
    const part = tiktok.slice(i, i + COMMENTS_BATCH_SIZE);
    batches.push({
      size: part.length,
      run: () =>
        fetchTikTok
          ? fetchTikTok(part)
          : Promise.resolve({
              comments: [],
              errors: part.map((p) => ({ mediaId: p.videoId, error: 'Комментарии TikTok здесь не поддерживаются' })),
              truncated: [],
            }),
    });
  }
  let done = 0;
  for (const batch of batches) {
    done += batch.size;
    onProgress(batches.length > 1 ? `Комментарии: ${done} из ${wanted.length}...` : 'Комментарии...');
    const res = await batch.run();
    for (const comment of res.comments) {
      const list = byMedia.get(comment.mediaId);
      if (list) list.push(comment);
      else byMedia.set(comment.mediaId, [comment]);
    }
    errors.push(...res.errors);
    truncated.push(...res.truncated);
  }

  const withComments = await Promise.all(
    groups.map(async (group) => ({
      ...group,
      comments: await pickTopComments(
        group.items.flatMap((item) => byMedia.get(item.mediaId) ?? []),
        options,
      ),
    })),
  );

  return { groups: withComments, errors, truncated };
}
